import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaHotel, FaMoneyBillWave, FaCheckCircle, FaUserAlt, FaFilter, FaList } from 'react-icons/fa';
import { getUserById, getAdminProperties } from '../../../api/services/adminService';
import { getPropertyRentStatus } from '../../../api/services/billingService';
import UserAvatar from './UserAvatar';
import { SectionHeader, Badge, EmptyState, LoadingSkeleton, Alert } from './AdminUI';
import './PropertyDetails.css'; // Leverage existing admin detail styles

const AirbnbHostDetails = () => {
  const { hostId } = useParams();
  const navigate = useNavigate();

  const [host, setHost] = useState(null);
  const [properties, setProperties] = useState([]);
  const [rentStatuses, setRentStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userRes = await getUserById(hostId);
      setHost(userRes?.data || userRes);

      const propsRes = await getAdminProperties({ ownerId: hostId, size: 100 });
      const propsList = propsRes?.data?.content || propsRes?.content || [];
      setProperties(propsList);

      // Load rent statuses for all properties in parallel
      const now = new Date();
      const statusPromises = propsList.map(async (prop) => {
        try {
          const statusRes = await getPropertyRentStatus(prop.id, { year: now.getFullYear(), month: now.getMonth() + 1 });
          return { id: prop.id, data: statusRes?.data || statusRes };
        } catch (e) {
          console.error(`Failed to load rent status for property ${prop.id}`, e);
          return { id: prop.id, data: null };
        }
      });

      const resolved = await Promise.all(statusPromises);
      const statusMap = {};
      resolved.forEach((item) => {
        if (item.data) {
          statusMap[item.id] = item.data;
        }
      });
      setRentStatuses(statusMap);

    } catch (e) {
      setError(e?.message || 'Failed to load host details');
    } finally {
      setLoading(false);
    }
  }, [hostId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aggregate Stats
  const stats = useMemo(() => {
    let expected = 0;
    let collected = 0;
    let outstanding = 0;
    let totalUnits = 0;
    let occupiedUnits = 0;

    properties.forEach((prop) => {
      const rentData = rentStatuses[prop.id];
      if (rentData) {
        expected += rentData.totalExpected || 0;
        collected += rentData.totalCollected || 0;
        outstanding += rentData.totalOutstanding || 0;
        totalUnits += rentData.items ? rentData.items.length : (prop.unitCount || 0);
        occupiedUnits += rentData.items ? rentData.items.filter(i => i.paymentStatus === 'PAID' || i.paymentStatus === 'PARTIAL').length : (prop.occupiedUnits || 0);
      } else {
        expected += prop.totalMonthlyRentExpected || 0;
        totalUnits += prop.unitCount || 0;
        occupiedUnits += prop.occupiedUnits || 0;
      }
    });

    return {
      expected,
      collected,
      outstanding,
      totalUnits,
      occupiedUnits,
      vacantUnits: Math.max(0, totalUnits - occupiedUnits),
    };
  }, [properties, rentStatuses]);

  // Filtered Properties
  const filteredProperties = useMemo(() => {
    return properties.filter((prop) => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = prop.name?.toLowerCase().includes(q);
        const matchesAddress = prop.address?.toLowerCase().includes(q);
        if (!matchesName && !matchesAddress) return false;
      }
      // Visibility filter
      if (visibilityFilter !== 'ALL') {
        const isPub = visibilityFilter === 'PUBLISHED';
        if (prop.published !== isPub) return false;
      }
      // Status filter
      if (statusFilter !== 'ALL') {
        const isActive = statusFilter === 'ACTIVE';
        if (prop.isActive !== isActive) return false;
      }
      return true;
    });
  }, [properties, searchQuery, visibilityFilter, statusFilter]);

  const verifBadge = (status) => {
    if (!status || status === 'NONE') return <Badge status="neutral">Not verified</Badge>;
    if (status === 'APPROVED' || status === 'VERIFIED') return <Badge status="success">Verified</Badge>;
    if (status === 'REJECTED') return <Badge status="danger">Rejected</Badge>;
    return <Badge status="warning">Pending</Badge>;
  };

  const formatMoney = (val) => {
    return `KES ${Number(val || 0).toLocaleString('en-KE', { maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="pd-container">
        <button type="button" className="pd-back" onClick={() => navigate('/system-admin/airbnb-hosts')}>
          <FaArrowLeft /> Back to Hosts
        </button>
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  if (error || !host) {
    return (
      <div className="pd-container">
        <button type="button" className="pd-back" onClick={() => navigate('/system-admin/airbnb-hosts')}>
          <FaArrowLeft /> Back to Hosts
        </button>
        <Alert type="error">{error || 'Host not found.'}</Alert>
      </div>
    );
  }

  return (
    <div className="pd-container">
      <button type="button" className="pd-back" onClick={() => navigate('/system-admin/airbnb-hosts')}>
        <FaArrowLeft /> Back to Hosts
      </button>

      {/* Header Banner */}
      <div className="pd-header">
        <div className="pd-header-media" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ecf0f9' }}>
          <UserAvatar
            profilePhotoPath={host.profilePhotoPath}
            firstName={host.firstName}
            lastName={host.lastName}
            size={70}
          />
        </div>
        <div className="pd-header-body">
          <div className="pd-header-title">
            <h1>{host.firstName} {host.lastName}</h1>
            <Badge status={host.isActive ? 'success' : 'danger'}>
              {host.isActive ? 'Active User' : 'Suspended'}
            </Badge>
          </div>
          <p className="pd-header-sub">{host.email} • {host.phone || 'No phone number'}</p>
        </div>
      </div>

      {/* Stats Summary Strip */}
      <div className="pd-stats" style={{ marginTop: 20 }}>
        <div className="pd-stat">
          <span className="pd-stat-value">{properties.length}</span>
          <span className="pd-stat-label">Airbnb Listings</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{stats.totalUnits}</span>
          <span className="pd-stat-label">Total rooms/units</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{stats.occupiedUnits}</span>
          <span className="pd-stat-label">Booked/Occupied</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value">{formatMoney(stats.expected)}</span>
          <span className="pd-stat-label">Expected revenue (this month)</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value" style={{ color: 'var(--admin-success)' }}>{formatMoney(stats.collected)}</span>
          <span className="pd-stat-label">Collected earnings</span>
        </div>
        <div className="pd-stat">
          <span className="pd-stat-value" style={{ color: 'var(--admin-warning)' }}>{formatMoney(stats.outstanding)}</span>
          <span className="pd-stat-label">Outstanding balance</span>
        </div>
      </div>

      <div className="pd-columns" style={{ marginTop: 24 }}>
        {/* Left column: properties list and details */}
        <div className="pd-col-main" style={{ flex: 2 }}>
          <div className="admin-card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
              <h2 className="admin-card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaHotel /> Listings ({filteredProperties.length})
              </h2>
            </div>

            {/* Filter Panel */}
            <div className="filter-panel" style={{ padding: 14, background: '#f9fafb', borderRadius: 10, marginBottom: 16, border: '1px solid #e5e7eb' }}>
              <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                <div className="filter-item">
                  <label className="filter-label" style={{ fontSize: 10 }}>Search properties</label>
                  <input
                    type="text"
                    className="filter-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search name or address..."
                    style={{ padding: 8, fontSize: 12 }}
                  />
                </div>
                <div className="filter-item">
                  <label className="filter-label" style={{ fontSize: 10 }}>Visibility</label>
                  <select
                    className="filter-select"
                    value={visibilityFilter}
                    onChange={(e) => setVisibilityFilter(e.target.value)}
                    style={{ padding: 8, fontSize: 12 }}
                  >
                    <option value="ALL">All</option>
                    <option value="PUBLISHED">Published Only</option>
                    <option value="UNPUBLISHED">Unpublished Only</option>
                  </select>
                </div>
                <div className="filter-item">
                  <label className="filter-label" style={{ fontSize: 10 }}>Status</label>
                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ padding: 8, fontSize: 12 }}
                  >
                    <option value="ALL">All</option>
                    <option value="ACTIVE">Active Only</option>
                    <option value="BLOCKED">Blocked Only</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredProperties.length === 0 ? (
              <EmptyState icon="🏨" title="No listings found" text="Try clearing search query or filters." />
            ) : (
              <div className="admin-table-wrap" style={{ border: '1px solid #e5e7eb', borderRadius: 12 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Listing</th>
                      <th>Expected Revenue</th>
                      <th>Collected</th>
                      <th>Outstanding</th>
                      <th>Occupancy</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProperties.map((prop) => {
                      const rentData = rentStatuses[prop.id];
                      const propExpected = rentData ? rentData.totalExpected : (prop.totalMonthlyRentExpected || 0);
                      const propCollected = rentData ? rentData.totalCollected : 0;
                      const propOutstanding = rentData ? rentData.totalOutstanding : propExpected;
                      const propTotalUnits = rentData && rentData.items ? rentData.items.length : (prop.unitCount || 0);
                      const propOccupiedUnits = rentData && rentData.items ? rentData.items.filter(i => i.paymentStatus === 'PAID' || i.paymentStatus === 'PARTIAL').length : (prop.occupiedUnits || 0);

                      return (
                        <tr key={prop.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/system-admin/properties/${prop.id}`)}>
                          <td>
                            <div style={{ fontWeight: 600, color: '#02236A' }}>{prop.name}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{prop.address || 'No address listed'}</div>
                          </td>
                          <td style={{ fontWeight: 600 }}>{formatMoney(propExpected)}</td>
                          <td style={{ color: 'var(--admin-success)', fontWeight: 600 }}>{formatMoney(propCollected)}</td>
                          <td style={{ color: 'var(--admin-warning)', fontWeight: 600 }}>{formatMoney(propOutstanding)}</td>
                          <td>
                            <span style={{ fontWeight: 500 }}>{propOccupiedUnits} / {propTotalUnits}</span>
                            <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>booked</span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <Badge status={prop.isActive ? 'success' : 'danger'}>{prop.isActive ? 'Active' : 'Blocked'}</Badge>
                              <Badge status={prop.published ? 'info' : 'neutral'}>{prop.published ? 'Published' : 'Draft'}</Badge>
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="admin-btn admin-btn-outline"
                              style={{ padding: '6px 12px', fontSize: 11 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/system-admin/properties/${prop.id}`);
                              }}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right column: host profile information */}
        <div className="pd-col-side" style={{ flex: 1 }}>
          <div className="admin-card" style={{ padding: 20, marginBottom: 20 }}>
            <h2 className="admin-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <FaUserAlt /> Host Profile
            </h2>
            <div className="pd-info-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>National ID</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{host.idNumber || '—'}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>KYC Verification</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{verifBadge(host.kycStatus)}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>KYB (Business)</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{verifBadge(host.kybStatus)}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Joined Date</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{new Date(host.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="pd-info-item" style={{ display: 'flex', flexDirection: 'column', paddingBottom: 8 }}>
                <span className="pd-info-label" style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' }}>Last Login</span>
                <span className="pd-info-value" style={{ fontWeight: 600, fontSize: 13.5 }}>{host.lastLogin ? new Date(host.lastLogin).toLocaleString() : 'Never logged in'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AirbnbHostDetails;
